// Frontend/src/components/notifications/NotificationBell.tsx
//
// One bell, three layouts.
//
// All three headers are the same shape — `flex items-center justify-between`
// with a logout Button on the right — so this drops in beside logout
// unchanged in each. The `role` prop is the only thing that differs, and it
// only affects where a notification navigates to.
//
// This deliberately does not use `usePolling`: that hook throws outside
// <HoaAdminLayout /> ("usePolling must be used within <HoaAdminLayout />"),
// and two of the three places this renders are outside it.

import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCheck } from "lucide-react";

import {
  listNotifications,
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from "@/api/notifications";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  KIND_ICON,
  KIND_TINT,
  routeFor,
  useRelativeTime,
  type BellRole,
} from "@/lib/notificationDisplay";
import { notificationStream } from "@/lib/notificationStream";
import { playNotificationChime } from "@/lib/notificationSound";
import type { NotificationItem } from "@/types/notification";

export default function NotificationBell({ role }: { role: BellRole }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const relativeTime = useRelativeTime();

  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // connect() is idempotent and the bell mounts in all three layouts, so
  // whichever one the user lands in opens the one shared stream. No
  // disconnect on unmount: navigating between layouts (e.g. a role-based
  // redirect) should not tear down a live connection just because this
  // particular mount point went away -- only an actual logout should (see
  // notificationStream.ts's own "urbe360:logout" listener).
  React.useEffect(() => {
    notificationStream.connect();
  }, []);

  const { data: countData } = useUnreadCount();
  const { data: items, isLoading } = useNotifications(open);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const unread = countData?.unread ?? 0;

  // Announce a new arrival: a toast and a chime, not just a badge that
  // changed color while the resident was looking at something else.
  //
  // `null` on the ref means "we don't know the baseline yet" — the first
  // count this session, which is almost always a leftover unread inbox from
  // before, not something that just arrived. Only a rise *after* that
  // baseline is real news. useUnreadCount is what is already polling every
  // 30s; this does not add a second poller.
  const previousUnreadRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (countData === undefined) return;
    const previous = previousUnreadRef.current;
    previousUnreadRef.current = countData.unread;
    if (previous === null || countData.unread <= previous) return;

    playNotificationChime();

    // The count is all useUnreadCount carries. Naming what arrived costs one
    // request on this rare event (a rise between polls) — useNotifications
    // still only fetches while the popover is open, so this is not a second
    // background poller, just an occasional extra call.
    listNotifications(true)
      .then((unreadItems) => {
        const newest = unreadItems[0] as NotificationItem | undefined;
        toast.info(newest?.title || t("notifications.newArrived"), {
          description: newest?.body || undefined,
          duration: 6000,
          action: { label: t("notifications.view"), onClick: () => setOpen(true) },
        });
      })
      .catch(() => {
        toast.info(t("notifications.newArrived"), { duration: 6000 });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countData]);

  // Click-away and Escape. Without the first, the popover survives a click on
  // the page behind it and covers whatever the user was reaching for.
  React.useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function handleClick(item: NotificationItem) {
    if (!item.is_read) markRead.mutate(item.id);
    setOpen(false);

    const route = routeFor(item, role);
    if (route) navigate(route);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("notifications.title")}
        aria-expanded={open}
        title={t("notifications.title")}
        className="relative grid h-9 w-9 place-items-center rounded-xl bg-white text-gray-700 ring-1 ring-black/5 transition hover:bg-gray-50 active:bg-gray-100"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && (
          <span
            className="absolute -right-1 -top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white"
            style={{ background: "#DC2626" }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t("notifications.title")}
          className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/10"
        >
          <header className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <h2 className="text-[13px] font-semibold text-gray-900">
              {t("notifications.title")}
            </h2>
            {unread > 0 && (
              <span className="text-[11px] font-medium text-gray-500">
                {t("notifications.unreadCount", { count: unread })}
              </span>
            )}
          </header>

          <div className="max-h-[min(24rem,60vh)] overflow-y-auto overscroll-contain">
            {isLoading && (
              <p className="px-4 py-8 text-center text-[13px] text-gray-400">
                {t("common.loading")}
              </p>
            )}

            {!isLoading && (items ?? []).length === 0 && (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto mb-2 h-8 w-8 text-gray-200" />
                <p className="text-[13px] text-gray-500">{t("notifications.empty")}</p>
              </div>
            )}

            <ul>
              {(items ?? []).map((item) => {
                const Icon = KIND_ICON[item.kind] ?? Bell;
                const tint = KIND_TINT[item.kind] ?? "#64748B";
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => handleClick(item)}
                      className={`flex w-full items-start gap-3 border-b border-gray-50 px-4 py-3 text-left transition hover:bg-gray-50 ${
                        item.is_read ? "" : "bg-blue-50/40"
                      }`}
                    >
                      <span
                        className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-xl"
                        style={{ background: `${tint}14`, color: tint }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex items-start gap-2">
                          <span
                            className={`min-w-0 flex-1 truncate text-[13px] ${
                              item.is_read
                                ? "font-medium text-gray-700"
                                : "font-semibold text-gray-900"
                            }`}
                          >
                            {item.title}
                          </span>
                          {!item.is_read && (
                            <span
                              className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                              style={{ background: "#DC2626" }}
                              aria-hidden
                            />
                          )}
                        </span>
                        {item.body && (
                          <span className="mt-0.5 line-clamp-2 block text-[12px] text-gray-500">
                            {item.body}
                          </span>
                        )}
                        <span className="mt-1 block text-[11px] text-gray-400">
                          {relativeTime(item.created_at)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {(
            <footer className="flex items-center gap-2 border-t border-gray-100 bg-gray-50/80 px-3 py-2">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-[12px] font-semibold text-gray-700 transition hover:bg-white disabled:opacity-50"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  {t("notifications.markAllRead")}
                </button>
              )}
              {/* The popover only ever shows the newest 50 (inbox cap, no
                  pagination). A resident with more history than that has
                  nowhere else to look without this. */}
              {(
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/${role}/notifications`);
                  }}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-[12px] font-semibold text-gray-700 transition hover:bg-white"
                >
                  {t("notifications.viewAll")}
                </button>
              )}
            </footer>
          )}
        </div>
      )}
    </div>
  );
}
